import {
  ALL_SEXES,
  DELETION_FLAG,
  ERROR_CODE,
  PRIVACY_POLICY_URL,
  type ResidentModificationFormInput,
  ResidentModificationFormSchema,
  SEX,
  UniversalError,
  parse,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { Temporal } from "temporal-polyfill";

import {
  CheckboxStyle,
  DropDownList,
  ExternalLink,
  FormField,
  FormFieldContainer,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PaperStyle,
  SwitchStyle,
  TypographyStyle,
  buttonStyles,
  formFieldInputStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  modifyResident,
  removeResidentInfiniteQueryCache,
  updateResidentQueryData,
  useFacilityQuery,
  useGetErrorMessage,
  useResident,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/residents/$userKey/modification",
)({ component: Component });

function Component() {
  const { facilityKey, userKey } = Route.useParams();

  const { data: facility } = useFacilityQuery(facilityKey);
  const getErrorMessage = useGetErrorMessage();
  const router = useRouter();
  const translate = useTranslate();
  const resident = useResident(userKey);

  const schema = ResidentModificationFormSchema();
  const defaultValues = useMemo(
    function (): ResidentModificationFormInput {
      return {
        agreed: resident?.agreed ?? false,
        birthday:
          resident?.birthday.toString() ??
          Temporal.Now.plainDateISO("Asia/Tokyo").subtract({ years: 75 }).toString(),
        deleted:
          resident?.deleted === DELETION_FLAG.DEFAULT
            ? DELETION_FLAG.DEFAULT
            : DELETION_FLAG.ARCHIVED,
        displayName: resident?.displayName ?? "",
        height: resident?.height ?? "",
        name: resident?.name ?? "",
        pronunciationName: resident?.pronunciationName ?? "",
        sex: resident?.sex ?? SEX.MALE,
        weight: resident?.weight ?? "",
      };
    },
    [resident],
  );

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      if (!resident) {
        showError(new UniversalError(ERROR_CODE.RESIDENT_NOT_FOUND));
        return;
      }

      try {
        console.debug("入力", input);
        const output = parse(schema, input);
        console.debug("出力", output);

        if (output.deleted !== DELETION_FLAG.DEFAULT) {
          if (
            !(await $confirm({
              color: "warning",
              content: translate("%ResidentModification.1%"),
              no: translate("%Cancel.0%"),
              title: translate("%ResidentModification.2%"),
              yes: translate("%Disable.0%"),
            }))
          ) {
            return;
          }
        }

        const result = await modifyResident({ ...output, userKey });

        await $alert({
          color: "success",
          content: translate("%ResidentModification.0%", { $: { name: result.resident.name } }),
          title: translate("%Complete.0%"),
        });

        updateResidentQueryData(result.resident.userKey, result.resident);
        removeResidentInfiniteQueryCache();

        if (router.history.canGoBack()) {
          router.history.back();
        } else {
          await router.navigate({
            ignoreBlocker: true,
            params: { facilityKey },
            to: "/facilities/$facilityKey/residents",
          });
        }
      } catch (error) {
        showError(error);
      }
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: schema },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <main
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "24px 32px" },
      })}
    >
      <PageName>
        {facility && resident ? resident.name + " - " + facility.name : translate("%Loading.0%")}
      </PageName>

      <form
        className={css(PaperStyle.raw({ borderRadius: "12px" }), {
          maxWidth: "max-content",
          padding: "16px 24px",
        })}
        onSubmit={function (event) {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <h1 className={css({ fontSize: "18px", fontWeight: 600 })}>
          {translate("%ResidentInformation.0%")}
        </h1>

        <form.Field name="deleted" validators={{ onChange: schema.entries.deleted }}>
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                description={translate("%Status.2%")}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%Status.0%")}
                required
              >
                <FormFieldInput
                  checked={field.state.value === DELETION_FLAG.DEFAULT}
                  className={SwitchStyle({ display: "block" })}
                  onChange={function (event) {
                    field.handleChange(
                      event.target.checked ? DELETION_FLAG.DEFAULT : DELETION_FLAG.ARCHIVED,
                    );
                  }}
                  type="checkbox"
                />
              </FormField>
            );
          }}
        </form.Field>

        <FormField
          className={css({ marginTop: "16px", width: "100%" })}
          label={translate("%UserKey.0%")}
        >
          <FormFieldInput
            className={css(
              TypographyStyle.raw({ monospace: true }),
              FormFieldInputStyle.raw({ width: "100%" }),
            )}
            disabled
            value={resident?.userKey ?? ""}
          />
        </FormField>

        <form.Field name="name" validators={{ onChange: schema.entries.name }}>
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "16px", maxWidth: "320px" })}
                description={translate("%Name.1%")}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%Name.0%")}
                required
              >
                <FormFieldInput
                  className={css(FormFieldInputStyle.raw({}), { width: "100%" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </form.Field>

        <form.Field name="displayName" validators={{ onChange: schema.entries.displayName }}>
          {function (field) {
            return (
              <FormFieldContainer
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ marginTop: "16px", maxWidth: "320px" })}
                description={translate("%DisplayName.1%")}
                invalid={!field.state.meta.isValid}
                label={translate("%DisplayName.0%")}
              >
                <FormFieldInput
                  className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={(event) => field.handleChange(event.target.value)}
                  value={field.state.value}
                />
              </FormFieldContainer>
            );
          }}
        </form.Field>

        <form.Field
          name="pronunciationName"
          validators={{ onChange: schema.entries.pronunciationName }}
        >
          {function (field) {
            return (
              <FormFieldContainer
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ marginTop: "16px", maxWidth: "320px" })}
                description={translate("%PronunciationName.1%")}
                invalid={!field.state.meta.isValid}
                label={translate("%PronunciationName.0%")}
              >
                <FormFieldInput
                  className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={(event) => field.handleChange(event.target.value)}
                  value={field.state.value}
                />
              </FormFieldContainer>
            );
          }}
        </form.Field>

        <form.Field name="sex" validators={{ onChange: schema.entries.sex }}>
          {function (field) {
            return (
              <FormFieldContainer
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ marginTop: "16px", maxWidth: "320px" })}
                invalid={!field.state.meta.isValid}
                label={translate("%Sex.0%")}
              >
                <DropDownList
                  className={css({ maxWidth: "320px" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={function (event) {
                    field.handleChange(event.target.value as any);
                  }}
                  required
                  value={field.state.value}
                >
                  {ALL_SEXES.map(function (sex) {
                    return (
                      <option key={sex} value={sex}>
                        {translate("%Sex." + sex + ".0%")}
                      </option>
                    );
                  })}
                </DropDownList>
              </FormFieldContainer>
            );
          }}
        </form.Field>

        <form.Field name="birthday" validators={{ onChange: schema.entries.birthday }}>
          {function (field) {
            return (
              <FormFieldContainer
                caution={getErrorMessage(field.state.meta.errors.at(0))}
                className={css({ marginTop: "16px", maxWidth: "160px" })}
                invalid={!field.state.meta.isValid}
                label={translate("%Birthday.0%")}
              >
                <FormFieldInput
                  className={css(formFieldInputStyles.raw({}), { width: "100%" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={(event) => field.handleChange(event.target.value)}
                  required
                  type="date"
                  value={typeof field.state.value === "string" ? field.state.value : ""}
                />
              </FormFieldContainer>
            );
          }}
        </form.Field>

        <div
          className={css({
            alignItems: "flex-start",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "16px",
            maxWidth: "320px",
          })}
        >
          <form.Field name="height" validators={{ onChange: schema.entries.height }}>
            {function (field) {
              return (
                <FormFieldContainer
                  caution={getErrorMessage(field.state.meta.errors.at(0))}
                  className={css({ minWidth: 0 })}
                  invalid={!field.state.meta.isValid}
                  label={translate("%Height.0%")}
                  required
                >
                  <div
                    className={css({
                      alignItems: "flex-end",
                      display: "flex",
                      gap: "4px",
                      maxWidth: "100%",
                    })}
                  >
                    <FormFieldInput
                      className={css(formFieldInputStyles.raw({}), { minWidth: 0 })}
                      id={field.name}
                      min={0}
                      onBlur={() => field.handleBlur()}
                      onChange={(event) => field.handleChange(event.target.value)}
                      step={0.1}
                      type="number"
                      value={field.state.value}
                    />
                    <span className={css({ fontSize: "14px" })}>cm</span>
                  </div>
                </FormFieldContainer>
              );
            }}
          </form.Field>

          <form.Field name="weight" validators={{ onChange: schema.entries.weight }}>
            {function (field) {
              return (
                <FormFieldContainer
                  caution={getErrorMessage(field.state.meta.errors.at(0))}
                  className={css({ minWidth: 0 })}
                  invalid={!field.state.meta.isValid}
                  label={translate("%Weight.0%")}
                  required
                >
                  <div
                    className={css({
                      alignItems: "flex-end",
                      display: "flex",
                      gap: "4px",
                      maxWidth: "100%",
                    })}
                  >
                    <FormFieldInput
                      className={css(formFieldInputStyles.raw({}), { minWidth: 0 })}
                      id={field.name}
                      min={0}
                      onBlur={() => field.handleBlur()}
                      onChange={(event) => field.handleChange(event.target.value)}
                      step={0.1}
                      type="number"
                      value={field.state.value}
                    />
                    <span className={css({ fontSize: "14px" })}>kg</span>
                  </div>
                </FormFieldContainer>
              );
            }}
          </form.Field>
        </div>

        <form.Field name="agreed" validators={{ onChange: schema.entries.agreed }}>
          {function (field) {
            return (
              <label
                className={css({
                  alignItems: "center",
                  columnGap: "4px",
                  display: "grid",
                  gridTemplateColumns: "min-content max-content",
                  marginTop: "16px",
                })}
              >
                <input
                  checked={field.state.value ?? false}
                  className={CheckboxStyle({ height: "16px" })}
                  onBlur={function () {
                    field.handleBlur();
                  }}
                  onChange={function (event) {
                    field.handleChange(event.target.checked);
                  }}
                  type="checkbox"
                />
                <span
                  className={TypographyStyle({
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                  })}
                >
                  {translate("%PrivacyPolicy.1%")}
                </span>
                <div />

                <p
                  className={TypographyStyle({
                    color: "primary/60",
                    fontSize: "14px",
                    lineHeight: 1,
                    marginTop: "8px",
                  })}
                >
                  <ExternalLink
                    className={css({ textDecoration: "underline" })}
                    href={PRIVACY_POLICY_URL}
                  >
                    {translate("%PrivacyPolicy.0%")}
                  </ExternalLink>
                  <span>{translate("%Confirm.0%")}</span>
                </p>
              </label>
            );
          }}
        </form.Field>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <Link
            className={css(buttonStyles.raw({ variant: "outline" }), { width: "100%" })}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/residents"
          >
            {translate("%Cancel.0%")}
          </Link>

          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={css(buttonStyles.raw({ color: "primary" }), { width: "100%" })}
                  disabled={submitting}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </form>
    </main>
  );
}
